// ─────────────────────────────────────────────────────────────────────────────
// SelectionController
// ─────────────────────────────────────────────────────────────────────────────
// The `isAllSelected` / `isPartialSelected` / `toggleAll` / `toggleOne` triad is
// hand-written on ~30 list surfaces, each with its own storage shape and its own
// subtly different pruning rules. This bundles the triad, the id storage and a
// `RangeSelectionTracker` behind one object so a table only has to say *what* a
// row's id is.
//
// It owns the two invariants that are easy to get wrong when written by hand:
//
//   1. Selection never outlives the rendered page. Ids that scroll out on a page
//      or filter change are dropped, so a bulk action can never reach a row the
//      user cannot see.
//   2. The range anchor is an *index*, so it is reset whenever the rendered rows
//      actually change order or membership — but NOT on a re-emission of the
//      same list, which would break a shift-extend mid-gesture.
//
// Feed it the *selectable* rows only. Rows that render no checkbox must never be
// passed in, or a shift-range would silently select them.
//
// Usage:
//   readonly selection = new SelectionController<IBrand>((brand) => brand.id);
//
//   this.brands$.subscribe((brands) => this.selection.setRows(brands));
//
//   <ui-checkbox
//     [checked]="selection.isSelected(brand.id)"
//     (changed)="selection.toggleOne(brand.id, $event.checked, $event)">
//   </ui-checkbox>
// ─────────────────────────────────────────────────────────────────────────────

import { NO_SELECTION_MODIFIERS, SelectionModifiers } from '../models/selection.models';
import { RangeSelectionTracker } from './range-selection';

/** Anything usable as a row identity. `Set` membership is by value for both. */
export type SelectionId = number | string;

export class SelectionController<T, K extends SelectionId = number> {
  /** Ids of the currently rendered selectable rows, in render order. */
  private rowIds: K[] = [];

  private selected = new Set<K>();

  private readonly range = new RangeSelectionTracker();

  /** @param idOf Row → identity. Must be stable for the lifetime of the row. */
  constructor(private readonly idOf: (row: T) => K) {}

  // ── Rendered rows ──────────────────────────────────────────────────────────

  /**
   * Sync with the rows currently on screen. Call it from wherever the list
   * arrives — a store subscription, a resolver, a manual refresh.
   */
  setRows(rows: T[] | null | undefined): void {
    const nextIds = (rows ?? []).map((row) => this.idOf(row));

    // Same ids in the same order means a re-emission, not a real list change.
    // Only a genuine change invalidates the anchor.
    const changed = nextIds.length !== this.rowIds.length || nextIds.some((id, i) => id !== this.rowIds[i]);

    this.rowIds = nextIds;

    if (changed) {
      const onScreen = new Set<K>(nextIds);
      this.selected = new Set([...this.selected].filter((id) => onScreen.has(id)));
      this.range.reset();
    }
  }

  // ── Reads ──────────────────────────────────────────────────────────────────

  /** The selected ids, in insertion order. A copy — safe to mutate. */
  get ids(): K[] {
    return [...this.selected];
  }

  get count(): number {
    return this.selected.size;
  }

  /** How many selectable rows are currently rendered. */
  get rowCount(): number {
    return this.rowIds.length;
  }

  get hasSelection(): boolean {
    return this.selected.size > 0;
  }

  isSelected(id: K): boolean {
    return this.selected.has(id);
  }

  /** True only when every rendered row is selected, so an empty list is false. */
  get isAllSelected(): boolean {
    return this.rowIds.length > 0 && this.rowIds.every((id) => this.selected.has(id));
  }

  /** The tri-state "some but not all" visual for a select-all header. */
  get isPartialSelected(): boolean {
    return this.selected.size > 0 && !this.isAllSelected;
  }

  // ── Writes ─────────────────────────────────────────────────────────────────

  /**
   * Toggle one row, honouring the modifiers held during the activation. Pass the
   * `ui-checkbox` `changed` payload straight through as `modifiers`.
   */
  toggleOne(id: K, checked: boolean, modifiers: SelectionModifiers = NO_SELECTION_MODIFIERS): void {
    const affected = this.range.resolve(this.rowIds, this.rowIds.indexOf(id), modifiers);

    affected.forEach((rowId) => {
      if (checked) this.selected.add(rowId);
      else this.selected.delete(rowId);
    });
  }

  /** Select-all header. Not a row click, so it must not leave a stale pivot. */
  toggleAll(checked: boolean): void {
    this.selected = checked ? new Set(this.rowIds) : new Set<K>();
    this.range.reset();
  }

  /** Empty the selection — the bulk bar's clear affordance, and post-action reset. */
  clear(): void {
    this.toggleAll(false);
  }
}
